import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  TextField,
  CircularProgress,
} from "@mui/material";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";
import { useState, useEffect } from "react";
import { ArrowLeft, } from "lucide-react";

const HotelUpdatePayment = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hotelId } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { fetchDraft, hotelData, loading, saveStep } = useHotel();
  const [showSuccessPaymentModal, setshowSuccessPaymentModal] = useState(false);

  const rowLabelSx = { fontSize: isMobile ? "12px" : "13px", color: "#999", width: isMobile ? "100%" : "160px", flexShrink: 0 };
  const rowValueSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const [billingAddress, setBillingAddress] = useState({
    streetAddress: "",
    unit: "",
    city: "",
    region: "",
    postalCode: "",
  });

  const [payment, setPayment] = useState({
    accountName: "",
    bankName: "",
    branchCode: "",
    accountNumber: "",
  });

  const handleInputChange = (field, value) => {
    setPayment((prev) => ({ ...prev, [field]: value }));
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const labelSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 };
  const descSx = { fontSize: isMobile ? "12px" : "13px", color: "#999", mb: 1.5 };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
  };

  const isNextDisabled =
    !billingAddress.streetAddress || !billingAddress.city || !billingAddress.region || !billingAddress.postalCode
    || !payment.accountName || !payment.bankName || !payment.branchCode || !payment.accountNumber;

  useEffect(() => {
    if (hotelId) {
      fetchDraft(hotelId);
    }
  }, [hotelId, fetchDraft]);

  useEffect(() => {
    if (hotelData.billingAddress) {
      setBillingAddress(hotelData.billingAddress);
    }
  }, [hotelData]);

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  const handleSave = async () => {
    try {
      const BillingAddressForm = {
        streetAddress: billingAddress.streetAddress,
        unit: billingAddress.unit,
        city: billingAddress.city,
        region: billingAddress.region,
        postalCode: billingAddress.postalCode,
      };

      // Save additional services step
      await saveStep("billingAddress", BillingAddressForm);

      const paymentForm = {
        accountName: payment.accountName,
        bankName: payment.bankName,
        branchCode: payment.branchCode,
        accountNumber: payment.accountNumber,
      };

      // Save additional services step
      await saveStep("payment", paymentForm);

      setshowSuccessPaymentModal(true);
    } catch (err) {
      console.error("Error saving additional services:", err);
    }
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
              <Button
                onClick={() => navigate(-1)}
                sx={{
                  minWidth: "auto",
                  width: isMobile ? "36px" : "44px",
                  height: isMobile ? "36px" : "44px",
                  borderRadius: "50%",
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  padding: 0,
                  alignSelf: "flex-start",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                }}
              >
                <ArrowLeft size={isMobile ? 18 : 22} />
              </Button>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
                  {t('StepPaymentMethod')}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "16px" : "18px", color: "#666" }}>
                  {t('PaymentReviewDesc')}
                </Typography>
              </Box>
            </Box>
            <Button
              sx={{
                backgroundColor: "#31BACD",
                color: "#fff",
                padding: "10px 12px",
                borderRadius: "6px",
                textTransform: "none",
                fontSize: isMobile ? "12px" : "14px",
                lineHeight: 1,
                "&:hover": { backgroundColor: "#8ec3ca" },
                "&:disabled": { opacity: 0.5, color: "#fff" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                alignSelf: "flex-start",
                gap: 1,
                fontWeight: "700",
              }}
              onClick={handleSave}
              disabled={isNextDisabled}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <img src="/icons/save.svg" alt="lock" style={{ width: "20px", height: "auto" }} />}
              <span>{t('Save')}</span>
            </Button>
          </Box>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
              <Box>
                <Typography sx={labelSx}>
                  {t('AccountNameLabel')}{" "}
                  <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                    {t('Required')}
                  </Box>
                </Typography>
                <Typography sx={descSx}>{t('AccountNameDesc')}</Typography>
                <TextField
                  fullWidth
                  value={payment.accountName}
                  onChange={(e) => handleInputChange("accountName", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('BankNameLabel')}{" "}
                  <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                    {t('Required')}
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  value={payment.bankName}
                  onChange={(e) => handleInputChange("bankName", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('BranchCodeLabel')}{" "}
                  <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                    {t('Required')}
                  </Box>
                </Typography>
                <Typography sx={descSx}>{t('BranchCodeDesc')}</Typography>
                <TextField
                  fullWidth
                  value={payment.branchCode}
                  onChange={(e) => handleInputChange("branchCode", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('AccountNumberLabel')}{" "}
                  <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                    {t('Required')}
                  </Box>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                  <TextField
                    fullWidth
                    type="number"
                    value={payment.accountNumber}
                    onChange={(e) => handleInputChange("accountNumber", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('StreetAddressLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                <TextField
                  fullWidth
                  value={billingAddress.streetAddress}
                  onChange={(e) => handleInputChange("streetAddress", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={labelSx}>{t('UnitLabel')}
                </Typography>

                <TextField
                  fullWidth
                  value={billingAddress.unit}
                  onChange={(e) => handleInputChange("unit", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexDirection: isMobile ? "column" : "row" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={labelSx}>
                    {t('CityLabel')}<span style={{ color: "red" }}> *</span>

                  </Typography>
                  <TextField
                    fullWidth
                    value={billingAddress.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={labelSx}>
                    {t('RegionLabel')}<span style={{ color: "red" }}> *</span>

                  </Typography>
                  <TextField
                    fullWidth
                    value={billingAddress.region}
                    onChange={(e) => handleInputChange("region", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('PostalCodeLabel')}<span style={{ color: "red" }}> *</span>

                </Typography>
                <TextField
                  fullWidth
                  value={billingAddress.postalCode}
                  onChange={(e) => handleInputChange("postalCode", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

            </Box>
          )}
        </Box>
      </div>

      {(showSuccessPaymentModal) && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 2 : 4,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              width: isMobile ? "80%" : "auto",
              maxWidth: "700px",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Box sx={{ width: isMobile ? "100%" : "320px", flexShrink: 0, }}>
              <Box
                component="img"
                src="/icons/ppclogo.png"
                alt="Pet Pet Club"
                sx={{ height: isMobile ? "20px" : "24px", mb: 2 }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                <img src={"/icons/paymentUpdated.svg"} alt="lock" style={{ width: "56px", height: "auto" }} />
                <Typography sx={{ fontSize: isMobile ? "16px" : "14px", color: "#000", fontWeight: "700" }}>
                  {t('PaymentMethodUpdated')}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "40%", }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(`/hotel/${hotelId}/payment`)}
                    sx={{
                      backgroundColor: "#31BACD",
                      color: "#fff",
                      borderRadius: "10px",
                      textTransform: "none",
                      fontSize: isMobile ? "13px" : "14px",
                      padding: "8px 16px",
                      "&:hover": { backgroundColor: "#60bac6", },
                    }}
                  >
                    {t('Confirm')}
                  </Button>
                </Box>

              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Footer />
    </div>
  );
};

export default HotelUpdatePayment;
